import React from "react";
import { useTranslation } from "react-i18next";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Fade from "@mui/material/Fade";
import MDSnackbar from "components/MDSnackbar";
import { useState } from "react";
import {
  CircularProgress,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Autocomplete,
} from "@mui/material";
import appData from "layouts/products/data/appData";
import { useAddProduct } from "api/useProducts.ts";
import MDBox from "components/MDBox";
import { useMediaQuery, useTheme } from "@mui/material";
import { Box } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

function Tables() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const addProduct = useAddProduct();
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [packFilter, setPackFilter] = useState("all"); // "all", "packs", "non-packs"
  const [newProduct, setNewProduct] = useState({
    productName: {
      en: "",
      ar: "",
    },
    price: 0,
    stockQuantity: 0,
    category: "",
    productImg: null,
    description: {
      en: "",
      ar: "",
    },
    isPack: false,
    packProduct: "",
    packQuantity: 1,
    minSupplyQuantity: 0,
  });

  const {
    columns,
    rows,
    editedProduct,
    setEditedProduct,
    handleInputChange,
    handleSaveChanges,
    isModalOpen,
    isDeleteModalOpen,
    closeDeleteModal,
    handleCloseModal,
    notification,
    setNotification,
    handleCloseNotification,
    handleDeleteProduct,
    categories,
    handleUploadImage,
    handleFileChange,
    isFileModalOpen,
    closeFileModal,
    imagePreview,
    setImagePreview,
    isUploading,
    fetchedProducts,
  } = appData(packFilter);

  const handleAddProductClick = () => {
    setIsProductModalOpen(true); // Open modal for adding new product
  };

  const handleCloseAddProductModal = () => {
    setIsProductModalOpen(false); // Close modal
    setNewProduct({
      productName: {
        en: "",
        ar: "",
      },
      price: 0,
      stockQuantity: 0,
      category: "",
      productImg: null,
      description: {
        en: "",
        ar: "",
      },
      isPack: false,
      packProduct: "",
      packQuantity: 1,
      minSupplyQuantity: 0,
    });
  };
  const handleInputChangeNewProduct = (e) => {
    const { name, value, type } = e.target;
    if (name.includes(".")) {
      const [parentKey, childKey] = name.split(".");
      setNewProduct((prevState) => ({
        ...prevState,
        [parentKey]: {
          ...prevState[parentKey],
          [childKey]: value,
        },
      }));
    } else {
      setNewProduct((prevState) => ({
        ...prevState,
        [name]: type === "number" ? (value === "" ? "" : Number(value)) : value,
      }));
    }
  };

  const handleSaveProduct = async () => {
    // Validation for pack fields
    if (newProduct.isPack) {
      if (!newProduct.packProduct) {
        setNotification({
          open: true,
          message: t("products.validation.selectProductForPack"),
          severity: "error",
        });
        return;
      }
      if (!newProduct.packQuantity || newProduct.packQuantity < 1) {
        setNotification({
          open: true,
          message: t("products.validation.packQuantityMin"),
          severity: "error",
        });
        return;
      }
    }

    const productData = {
      name: {
        en: newProduct.productName.en,
        ar: newProduct.productName.ar,
      },
      price: newProduct.price,
      category: newProduct.category._id || newProduct.category,
      quantity: newProduct.stockQuantity,
      description: {
        en: newProduct.description.en,
        ar: newProduct.description.ar,
      },
      isPack: newProduct.isPack,
      ...(newProduct.isPack && {
        packProduct: newProduct.packProduct,
        packQuantity: parseInt(newProduct.packQuantity),
      }),
      ...(!newProduct.isPack && {
        minSupplyQuantity: newProduct.minSupplyQuantity,
      }),
    };

    addProduct.mutate(productData, {
      onSuccess: () => {
        setNotification({
          open: true,
          message: t("products.productAdded"),
          severity: "success",
        });
        handleCloseAddProductModal(); // Close modal on success
      },
      onError: (error) => {
        console.error("Error adding product:", error);
        setNotification({
          open: true,
          message: t("products.productAddFailed"),
          severity: "error",
        });
      },
    });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="dark"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                flexDirection={isMobile ? "column" : "row"}
                justifyContent="space-between"
                alignItems={isMobile ? "flex-start" : "center"}
              >
                <MDTypography variant="h6" color="white">
                  {t("products.title")}
                </MDTypography>
                <MDBox
                  display="flex"
                  flexDirection={isMobile ? "column" : "row"}
                  gap={isMobile ? 2 : "10px"}
                  width={isMobile ? "100%" : "auto"}
                >
                  <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>{t("common.filter")}</InputLabel>
                    <Select
                      value={packFilter}
                      onChange={(e) => setPackFilter(e.target.value)}
                      label={t("common.filter")}
                      sx={{
                        backgroundColor: "white",
                        borderRadius: 1,
                      }}
                    >
                      <MenuItem value="all">{t("products.filters.allProducts")}</MenuItem>
                      <MenuItem value="packs">{t("products.filters.packsOnly")}</MenuItem>
                      <MenuItem value="non-packs">{t("products.filters.nonPacks")}</MenuItem>
                    </Select>
                  </FormControl>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleAddProductClick}
                    fullWidth={isMobile} // Full width for mobile
                    sx={{
                      borderRadius: 2,
                      fontSize: 14,
                      padding: "8px 16px",
                      color: "black",
                    }}
                  >
                    {t("products.addProduct")}
                  </Button>
                </MDBox>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={true}
                  entriesPerPage={false}
                  showTotalEntries
                  noEndBorder
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
      <Dialog open={isProductModalOpen} onClose={handleCloseAddProductModal}>
        <DialogTitle>{t("products.addNewProduct")}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              {/* Product Name (English) */}
              <TextField
                name="productName.en"
                label={t("products.fields.productNameEn")}
                value={newProduct.productName.en}
                onChange={handleInputChangeNewProduct}
                fullWidth
                margin="dense"
              />
            </Grid>
            <Grid item xs={6}>
              {/* Product Name (Arabic) */}
              <TextField
                name="productName.ar"
                label={t("products.fields.productNameAr")}
                value={newProduct.productName.ar}
                onChange={handleInputChangeNewProduct}
                fullWidth
                margin="dense"
                dir="rtl" // Right-to-left direction for Arabic
              />
            </Grid>
          </Grid>

          {/* Category Selection */}
          <FormControl fullWidth margin="dense">
            <InputLabel id="category-label">{t("products.fields.category")}</InputLabel>
            <Select
              style={{ padding: "10px 0" }}
              labelId="category-label"
              name="category"
              value={newProduct.category?._id}
              onChange={handleInputChangeNewProduct}
              fullWidth
            >
              {categories?.map((category) => (
                <MenuItem key={category?._id} value={category?._id}>
                  {category.name.en} | {category.name.ar}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Price Input */}
          <TextField
            name="price"
            label={t("products.fields.price")}
            value={newProduct.price}
            onChange={handleInputChangeNewProduct}
            type="number"
            fullWidth
            margin="dense"
          />

          {/* Stock Quantity */}
          <TextField
            name="stockQuantity"
            label={t("products.fields.stockQuantity")}
            value={newProduct.stockQuantity}
            onChange={handleInputChangeNewProduct}
            type="number"
            fullWidth
            margin="dense"
          />

          {/* Min Supply Quantity */}
          {!newProduct.isPack && (
            <TextField
              name="minSupplyQuantity"
              label={t("products.fields.minSupplyQuantity")}
              value={newProduct.minSupplyQuantity}
              onChange={handleInputChangeNewProduct}
              type="number"
              inputProps={{ min: 0, step: 1 }}
              fullWidth
              margin="dense"
              helperText={t("products.fields.minSupplyQuantityHelp")}
            />
          )}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              {/* Description (English) */}
              <TextField
                name="description.en"
                label={t("products.fields.descriptionEn")}
                value={newProduct.description.en}
                onChange={handleInputChangeNewProduct}
                fullWidth
                margin="dense"
                multiline
                rows={4}
              />{" "}
            </Grid>
            <Grid item xs={6}>
              {/* Description (Arabic) */}
              <TextField
                name="description.ar"
                label={t("products.fields.descriptionAr")}
                value={newProduct.description.ar}
                onChange={handleInputChangeNewProduct}
                fullWidth
                margin="dense"
                multiline
                rows={4}
                dir="rtl" // Right-to-left direction for Arabic
              />
            </Grid>
          </Grid>

          {/* Pack Toggle */}
          <FormControlLabel
            control={
              <Checkbox
                checked={newProduct.isPack}
                onChange={(e) =>
                  setNewProduct((prev) => ({
                    ...prev,
                    isPack: e.target.checked,
                    packProduct: "",
                    packQuantity: 1,
                    ...(e.target.checked && { minSupplyQuantity: 0 }),
                  }))
                }
                name="isPack"
              />
            }
            label={t("products.fields.isPack")}
          />

          {/* Pack Fields */}
          {newProduct.isPack && (
            <>
              <Autocomplete
                options={(Array.isArray(fetchedProducts) ? fetchedProducts : []).map((product) => ({
                  _id: product._id,
                  name: product.name,
                  label: `${product.name?.en || ""} | ${product.name?.ar || ""}`,
                }))}
                getOptionLabel={(option) => option.label}
                value={
                  newProduct.packProduct
                    ? (Array.isArray(fetchedProducts) ? fetchedProducts : []).find(
                        (p) => p._id === newProduct.packProduct
                      )
                      ? {
                          _id: newProduct.packProduct,
                          name: (Array.isArray(fetchedProducts) ? fetchedProducts : []).find(
                            (p) => p._id === newProduct.packProduct
                          ).name,
                          label: `${
                            (Array.isArray(fetchedProducts) ? fetchedProducts : []).find(
                              (p) => p._id === newProduct.packProduct
                            ).name?.en || ""
                          } | ${
                            (Array.isArray(fetchedProducts) ? fetchedProducts : []).find(
                              (p) => p._id === newProduct.packProduct
                            ).name?.ar || ""
                          }`,
                        }
                      : null
                    : null
                }
                onChange={(event, newValue) => {
                  setNewProduct((prev) => ({ ...prev, packProduct: newValue?._id || "" }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("products.fields.packProduct")}
                    margin="dense"
                    fullWidth
                    required={newProduct.isPack}
                    helperText={t("products.fields.packProductHelp")}
                  />
                )}
              />

              <TextField
                name="packQuantity"
                label={t("products.fields.packQuantity")}
                value={newProduct.packQuantity}
                onChange={handleInputChangeNewProduct}
                type="number"
                inputProps={{ min: 1, step: 1 }}
                fullWidth
                margin="dense"
                required={newProduct.isPack}
                helperText={t("products.fields.packQuantityHelp")}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddProductModal} color="secondary">
            {t("common.cancel")}
          </Button>
          <Button onClick={handleSaveProduct} color="primary">
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Edit Product Modal */}
      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 500 }}
      >
        <DialogTitle>{t("products.editProductDetails")}</DialogTitle>
        {editedProduct && (
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  name="productName.en"
                  label={t("products.fields.productNameEn")}
                  value={editedProduct.productName.en}
                  onChange={handleInputChange}
                  fullWidth
                  margin="dense"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name="productName.ar"
                  label={t("products.fields.productNameAr")}
                  value={editedProduct.productName.ar}
                  onChange={handleInputChange}
                  fullWidth
                  margin="dense"
                  dir="rtl"
                />
              </Grid>
            </Grid>

            <FormControl fullWidth margin="dense">
              <InputLabel id="category-label">{t("products.fields.category")}</InputLabel>
              <Select
                style={{ padding: "10px 0" }}
                labelId="category-label"
                name="category"
                value={editedProduct.category?._id}
                onChange={handleInputChange}
                fullWidth
              >
                {categories &&
                  categories.map((category) => (
                    <MenuItem key={category?._id} value={category._id}>
                      {category.name.en} | {category.name.ar}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <TextField
              name="price"
              label={t("products.fields.price")}
              type="number"
              value={editedProduct.price}
              onChange={handleInputChange}
              fullWidth
              margin="dense"
            />

            <TextField
              name="stockQuantity"
              label={t("products.fields.stockQuantity")}
              type="number"
              value={editedProduct.stockQuantity}
              onChange={handleInputChange}
              fullWidth
              margin="dense"
            />

            {/* Min Supply Quantity */}
            {!editedProduct.isPack && (
              <TextField
                name="minSupplyQuantity"
                label={t("products.fields.minSupplyQuantity")}
                type="number"
                value={editedProduct.minSupplyQuantity || 0}
                onChange={handleInputChange}
                inputProps={{ min: 0, step: 1 }}
                fullWidth
                margin="dense"
                helperText={t("products.fields.minSupplyQuantityHelp")}
              />
            )}

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  name="description.en"
                  label={t("products.fields.descriptionEn")}
                  value={editedProduct.description.en}
                  onChange={handleInputChange}
                  fullWidth
                  margin="dense"
                  multiline
                  rows={4}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name="description.ar"
                  label={t("products.fields.descriptionAr")}
                  value={editedProduct.description.ar}
                  onChange={handleInputChange}
                  fullWidth
                  margin="dense"
                  multiline
                  rows={4}
                  dir="rtl"
                />
              </Grid>
            </Grid>

            {/* Pack Toggle */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={editedProduct.isPack}
                  onChange={(e) =>
                    setEditedProduct((prev) => ({
                      ...prev,
                      isPack: e.target.checked,
                      packProduct: "",
                      packQuantity: 1,
                      ...(e.target.checked && { minSupplyQuantity: 0 }),
                    }))
                  }
                  name="isPack"
                />
              }
              label={t("products.fields.isPack")}
            />

            {/* Pack Fields */}
            {editedProduct.isPack && (
              <>
                <Autocomplete
                  options={(Array.isArray(fetchedProducts) ? fetchedProducts : [])
                    .filter((product) => product._id !== editedProduct.productId)
                    .map((product) => ({
                      _id: product._id,
                      name: product.name,
                      label: `${product.name?.en || ""} | ${product.name?.ar || ""}`,
                    }))}
                  getOptionLabel={(option) => option.label}
                  value={
                    editedProduct.packProduct
                      ? (Array.isArray(fetchedProducts) ? fetchedProducts : []).find(
                          (p) => p._id === editedProduct.packProduct
                        )
                        ? {
                            _id: editedProduct.packProduct,
                            name: (Array.isArray(fetchedProducts) ? fetchedProducts : []).find(
                              (p) => p._id === editedProduct.packProduct
                            ).name,
                            label: `${
                              (Array.isArray(fetchedProducts) ? fetchedProducts : []).find(
                                (p) => p._id === editedProduct.packProduct
                              ).name?.en || ""
                            } | ${
                              (Array.isArray(fetchedProducts) ? fetchedProducts : []).find(
                                (p) => p._id === editedProduct.packProduct
                              ).name?.ar || ""
                            }`,
                          }
                        : null
                      : null
                  }
                  onChange={(event, newValue) => {
                    setEditedProduct((prev) => ({ ...prev, packProduct: newValue?._id || "" }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("products.fields.packProduct")}
                      margin="dense"
                      fullWidth
                      required={editedProduct.isPack}
                      helperText={t("products.fields.packProductHelp")}
                    />
                  )}
                />

                <TextField
                  name="packQuantity"
                  label={t("products.fields.packQuantity")}
                  value={editedProduct.packQuantity}
                  onChange={handleInputChange}
                  type="number"
                  inputProps={{ min: 1, step: 1 }}
                  fullWidth
                  margin="dense"
                  required={editedProduct.isPack}
                  helperText={t("products.fields.packQuantityHelp")}
                />
              </>
            )}
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={handleCloseModal} color="secondary">
            {t("common.cancel")}
          </Button>
          <Button onClick={() => handleSaveChanges(editedProduct)} color="primary">
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Confirm Deletion */}
      <Dialog
        open={isDeleteModalOpen}
        onClose={closeDeleteModal}
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 500 }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t("products.confirmDeletion")}</DialogTitle>
        <DialogContent>
          <MDTypography variant="body1" color="textSecondary">
            {t("products.deleteProductConfirm")}
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteModal} color="secondary">
            {t("common.cancel")}
          </Button>
          <Button onClick={handleDeleteProduct} color="primary">
            {t("common.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
      {/* File Modal */}
      <Dialog
        open={isFileModalOpen}
        onClose={closeFileModal}
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 500 }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t("products.editProductImage")}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
            {/* Image Preview */}
            {imagePreview && (
              <Box
                component="img"
                src={imagePreview}
                alt={t("products.productPreview")}
                sx={{
                  width: "100%",
                  height: "auto",
                  maxHeight: 300,
                  borderRadius: 2,
                  objectFit: "contain",
                  border: "1px solid #ccc",
                }}
              />
            )}
            <Button component="label" tabIndex={-1} startIcon={<CloudUploadIcon />} fullWidth>
              {t("products.uploadProductImage")}
              <input hidden accept="image/*" type="file" name="image" onChange={handleFileChange} />
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeFileModal} color="secondary">
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleUploadImage}
            color="primary"
            disabled={isUploading || !imagePreview}
            startIcon={isUploading ? <CircularProgress size={20} /> : null}
          >
            {isUploading ? t("common.uploading") : t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <MDSnackbar
        color={notification.severity}
        icon={notification.severity === "success" ? "check" : "error"}
        title={notification.severity === "success" ? t("common.success") : t("common.error")}
        content={notification.message}
        open={notification.open}
        onClose={handleCloseNotification}
        close={handleCloseNotification}
        bgWhite
      />
    </DashboardLayout>
  );
}

export default Tables;
